import {
  SITSTAT_DEFAULT_LAYOUT,
  SITSTAT_WIDGET_IDS,
  isSitStatWidgetId,
  type SitStatColumnId,
  type SitStatLayout,
  type SitStatWidgetId,
} from './sitstat-widget-types';
import type { SitStatTemplate } from './sitstat-template-types';
import { SITSTAT_SYSTEM_TEMPLATES } from './sitstat-system-templates';

export { SITSTAT_COLLAPSED_RAIL_WIDTH_PX } from './sitstat-widget-types';

// v2 namespace: the widget set was cut to six, so a v1 layout would survive the
// unknown-id filter as a near-empty pair of rails. Bumping hands everyone the
// new default once, after which removing a widget still sticks.
const LAYOUT_STORAGE_PREFIX = 'pratus.sitstat.layout.v2.';
const UI_STORAGE_PREFIX = 'pratus.sitstat.ui.v2.';
const TEMPLATES_STORAGE_PREFIX = 'pratus.sitstat.templates.v2.';
const ACTIVE_TEMPLATE_STORAGE_PREFIX = 'pratus.sitstat.active-template.v2.';

export type SitStatUiState = {
  layout: SitStatLayout;
  leftCollapsed: boolean;
  rightCollapsed: boolean;
};

/** Normalize a layout without forcing unused widgets onto rails. */
export function normalizeLayout(candidate: unknown): SitStatLayout {
  if (!candidate || typeof candidate !== 'object') {
    return structuredClone(SITSTAT_DEFAULT_LAYOUT);
  }

  const record = candidate as Partial<Record<SitStatColumnId, unknown>>;
  const columns: SitStatColumnId[] = ['left', 'center', 'right'];
  const next: SitStatLayout = {
    left: [],
    center: [],
    right: [],
  };
  const seen = new Set<SitStatWidgetId>();

  for (const column of columns) {
    const raw = record[column];
    if (!Array.isArray(raw)) continue;
    for (const entry of raw) {
      if (entry === 'map') continue;
      if (
        typeof entry !== 'string' ||
        !isSitStatWidgetId(entry) ||
        seen.has(entry)
      ) {
        continue;
      }
      next[column].push(entry);
      seen.add(entry);
    }
  }

  if (next.center.length > 0) {
    for (const id of next.center) {
      next.right.push(id);
    }
    next.center = [];
  }

  next.center = [];

  return next;
}

export function getUnusedSitStatWidgets(
  layout: SitStatLayout
): SitStatWidgetId[] {
  const used = new Set<SitStatWidgetId>([...layout.left, ...layout.right]);
  return SITSTAT_WIDGET_IDS.filter((id) => !used.has(id));
}

export function loadSitStatLayout(
  storageKey: string | null | undefined
): SitStatLayout {
  if (!storageKey || typeof window === 'undefined') {
    return structuredClone(SITSTAT_DEFAULT_LAYOUT);
  }

  try {
    const raw = window.localStorage.getItem(
      `${LAYOUT_STORAGE_PREFIX}${storageKey}`
    );
    if (!raw) return structuredClone(SITSTAT_DEFAULT_LAYOUT);
    return normalizeLayout(JSON.parse(raw) as unknown);
  } catch {
    return structuredClone(SITSTAT_DEFAULT_LAYOUT);
  }
}

export function saveSitStatLayout(
  storageKey: string | null | undefined,
  layout: SitStatLayout
): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(
      `${LAYOUT_STORAGE_PREFIX}${storageKey}`,
      JSON.stringify(normalizeLayout(layout))
    );
  } catch {
    // Ignore quota / private mode failures.
  }
}

function normalizeUiState(
  candidate: unknown,
  layoutFallback: SitStatLayout
): SitStatUiState {
  if (!candidate || typeof candidate !== 'object') {
    return {
      layout: layoutFallback,
      leftCollapsed: false,
      rightCollapsed: false,
    };
  }
  const record = candidate as Partial<SitStatUiState>;
  return {
    layout: record.layout ? normalizeLayout(record.layout) : layoutFallback,
    leftCollapsed: record.leftCollapsed === true,
    rightCollapsed: record.rightCollapsed === true,
  };
}

export function loadSitStatUiState(
  storageKey: string | null | undefined
): SitStatUiState {
  const layout = loadSitStatLayout(storageKey);
  if (!storageKey || typeof window === 'undefined') {
    return { layout, leftCollapsed: false, rightCollapsed: false };
  }
  try {
    const raw = window.localStorage.getItem(
      `${UI_STORAGE_PREFIX}${storageKey}`
    );
    if (!raw) return { layout, leftCollapsed: false, rightCollapsed: false };
    const parsed = normalizeUiState(JSON.parse(raw) as unknown, layout);
    return { ...parsed, layout };
  } catch {
    return { layout, leftCollapsed: false, rightCollapsed: false };
  }
}

export function saveSitStatUiState(
  storageKey: string | null | undefined,
  state: SitStatUiState
): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    const layout = normalizeLayout(state.layout);
    saveSitStatLayout(storageKey, layout);
    window.localStorage.setItem(
      `${UI_STORAGE_PREFIX}${storageKey}`,
      JSON.stringify({
        leftCollapsed: state.leftCollapsed === true,
        rightCollapsed: state.rightCollapsed === true,
      })
    );
  } catch {
    // Ignore quota / private mode failures.
  }
}

function normalizeUserTemplate(candidate: unknown): SitStatTemplate | null {
  if (!candidate || typeof candidate !== 'object') return null;
  const record = candidate as Partial<SitStatTemplate>;
  if (typeof record.id !== 'string' || typeof record.name !== 'string') {
    return null;
  }
  return {
    id: record.id,
    slug:
      typeof record.slug === 'string' && record.slug.trim()
        ? record.slug
        : record.id,
    name: record.name.trim() || 'Untitled view',
    layout: normalizeLayout(record.layout),
    uiState: {
      leftCollapsed: record.uiState?.leftCollapsed === true,
      rightCollapsed: record.uiState?.rightCollapsed === true,
    },
    isSystem: false,
  };
}

export function loadUserSitStatTemplates(
  storageKey: string | null | undefined
): SitStatTemplate[] {
  if (!storageKey || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(
      `${TEMPLATES_STORAGE_PREFIX}${storageKey}`
    );
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeUserTemplate)
      .filter((template): template is SitStatTemplate => template !== null);
  } catch {
    return [];
  }
}

export function saveUserSitStatTemplates(
  storageKey: string | null | undefined,
  templates: SitStatTemplate[]
): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    const userOnly = templates
      .filter((template) => !template.isSystem)
      .map((template) => ({
        id: template.id,
        slug: template.slug,
        name: template.name,
        layout: normalizeLayout(template.layout),
        uiState: {
          leftCollapsed: template.uiState.leftCollapsed === true,
          rightCollapsed: template.uiState.rightCollapsed === true,
        },
        isSystem: false,
      }));
    window.localStorage.setItem(
      `${TEMPLATES_STORAGE_PREFIX}${storageKey}`,
      JSON.stringify(userOnly)
    );
  } catch {
    // Ignore quota / private mode failures.
  }
}

export function listSitStatTemplates(
  storageKey: string | null | undefined
): SitStatTemplate[] {
  return [
    ...SITSTAT_SYSTEM_TEMPLATES.map((template) => ({
      ...template,
      layout: normalizeLayout(template.layout),
    })),
    ...loadUserSitStatTemplates(storageKey),
  ];
}

const AOR_TEMPLATE_ID_PREFIX = 'aor:';

export function aorSitStatTemplateId(aorId: string): string {
  return `${AOR_TEMPLATE_ID_PREFIX}${aorId}`;
}

export function isAorSitStatTemplateId(
  templateId: string | null | undefined
): boolean {
  return Boolean(templateId?.startsWith(AOR_TEMPLATE_ID_PREFIX));
}

/** Returns the AOR's template, creating it with the default layout on first use. */
export function ensureAorSitStatTemplate(
  storageKey: string | null | undefined,
  { aorId, name }: { aorId: string; name: string }
): SitStatTemplate | null {
  if (!storageKey || typeof window === 'undefined') return null;
  const id = aorSitStatTemplateId(aorId);
  const existing = loadUserSitStatTemplates(storageKey);
  const found = existing.find((template) => template.id === id);
  if (found) return found;

  const template: SitStatTemplate = {
    id,
    slug: id,
    name: name.trim() || 'AOR',
    layout: structuredClone(SITSTAT_DEFAULT_LAYOUT),
    uiState: { leftCollapsed: false, rightCollapsed: false },
    isSystem: false,
  };
  saveUserSitStatTemplates(storageKey, [...existing, template]);
  return template;
}

/** Writes layout and rail state onto an existing user template. */
export function updateUserSitStatTemplateState(
  storageKey: string | null | undefined,
  templateId: string,
  state: SitStatUiState
): SitStatTemplate[] {
  const templates = loadUserSitStatTemplates(storageKey);
  const next = templates.map((template) =>
    template.id === templateId
      ? {
          ...template,
          layout: normalizeLayout(state.layout),
          uiState: {
            leftCollapsed: state.leftCollapsed,
            rightCollapsed: state.rightCollapsed,
          },
        }
      : template
  );
  saveUserSitStatTemplates(storageKey, next);
  return next;
}

export function loadActiveSitStatTemplateId(
  storageKey: string | null | undefined
): string | null {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    return (
      window.localStorage.getItem(
        `${ACTIVE_TEMPLATE_STORAGE_PREFIX}${storageKey}`
      ) ?? null
    );
  } catch {
    return null;
  }
}

export function saveActiveSitStatTemplateId(
  storageKey: string | null | undefined,
  templateId: string | null
): void {
  if (!storageKey || typeof window === 'undefined') return;
  try {
    const key = `${ACTIVE_TEMPLATE_STORAGE_PREFIX}${storageKey}`;
    if (!templateId) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, templateId);
  } catch {
    // Ignore quota / private mode failures.
  }
}

export function resolveSitStatStorageKey(
  organizationId: string | null | undefined
): string {
  return organizationId ? `hub:${organizationId}` : 'hub';
}
